import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import {
  confirmUser,
  getUserToConfirmation,
  login,
} from '../services/LoginService';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { routes } from '../routes/routePaths';

import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Input from '../components/input/Input';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import { useClientLogo } from '../hooks/useClientLogo';
import { useSetConfig } from '../context/ConfigContext';
import {
  getPasswordPolicyChecks,
  isPasswordPolicyValid,
} from '../utils/passwordPolicy';

interface ExtendedJwtPayload extends JwtPayload {
  realm_access?: {
    roles?: string[];
  };
  email?: string;
  name?: string;
  given_name?: string;
}

const Confirmation = () => {
  const navigate = useNavigate();
  const setConfig = useSetConfig();
  const { id } = useParams();
  const { logoUrl } = useClientLogo();
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isNotFoundToken, setIsNotFoundToken] = useState(false);
  const [loadError, setLoadError] = useState('');

  const formik = useFormik({
    initialValues: {
      id: id || '',
      user: '',
      password: '',
      confirmPassword: '',
    },
    validateOnChange: true,
    validate: (values) => {
      return validate(values);
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      setErrorMsg('');

      try {
        const response = await confirmUser(
          id || '',
          values.password,
          values.confirmPassword
        );

        if (response.success) {
          const loginResponse = await login({
            user: values.user,
            password: values.password,
          });

          if (loginResponse.status === 200) {
            const decodedToken: ExtendedJwtPayload = jwtDecode(
              loginResponse.data.token
            );

            localStorage.setItem('token', loginResponse.data.token);
            localStorage.setItem(
              'refresh_token',
              loginResponse.data.refreshToken
            );
            localStorage.setItem('user', values.user);
            localStorage.setItem(
              'roles',
              decodedToken.realm_access?.roles?.join(',') ?? ''
            );
            localStorage.setItem('email', decodedToken.email ?? '');
            localStorage.setItem('full_name', decodedToken.name ?? '');
            localStorage.setItem('name', decodedToken.given_name ?? '');

            const configuration = loginResponse.data.configuration ?? {};
            setConfig(configuration);
            const roles = decodedToken.realm_access?.roles ?? [];

            if (roles.includes('PRODUCTOR')) {
              navigate(routes.dashboard);
            } else {
              navigate(routes.dashboardComercial);
            }

            window.location.reload();
          } else {
            setErrorMsg(loginResponse.message || 'Error al iniciar sesión');
          }
        } else {
          setErrorMsg(response.message || 'Error al confirmar el usuario');
        }
      } catch (err) {
        setErrorMsg('Error inesperado durante la confirmación');
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (id) {
      getUserToConfirmation(id).then((response) => {
        if (response.success) {
          formik.setFieldValue('user', response.data.email);
          formik.setFieldValue('id', id);
        } else if (response.status === 404) {
          setIsNotFoundToken(true);
        } else {
          setLoadError(
            'No se pudo validar el enlace. Intentá de nuevo más tarde.',
          );
        }
      });
    }
  }, [id]);

  const validate = (values: any) => {
    const errors: any = {};

    if (!values.user) {
      errors.user = 'El campo usuario es obligatorio';
    }

    if (!values.password) {
      errors.password = 'El campo contraseña es obligatorio';
    } else if (!isPasswordPolicyValid(values.password)) {
      errors.password =
        'La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula y un caracter especial';
    }

    if (!values.confirmPassword) {
      errors.confirmPassword = 'Debés confirmar la contraseña';
    } else if (values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Las contraseñas no coinciden';
    }

    return errors;
  };

  const passwordChecks = getPasswordPolicyChecks(formik.values.password);

  return (
    <Card
      variant='primary'
      borderRadius={false}
      modifier='regular'
      display='flex'
      justifyContent='center'
      height='100vh'
      width='100%'
      alignItems='center'
    >
      <Card
        boxShadow='1'
        spacing={{ top: '6', right: '4', bottom: '4', left: '4' }}
        width='360px'
        variant='neutral'
        height='auto'
        margin='auto'
        modifier='inverted'
      >
        <Card
          display='flex'
          justifyContent='center'
          margin={{ top: '6', bottom: '3' }}
        >
          <img
            src={logoUrl || 'assets/logos/logo.svg'}
            width='64px'
            alt='Logo'
          />
        </Card>

        {isNotFoundToken ? (
          <H
            withMargin={false}
            variant='danger'
            type='2'
            weight='bolder'
            align='center'
          >
            Lo sentimos, el link expiró
          </H>
        ) : loadError ? (
          <H
            withMargin={false}
            variant='danger'
            type='2'
            weight='bolder'
            align='center'
          >
            {loadError}
          </H>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Card margin={{ top: '2' }}>
              <Card margin={{ bottom: '3' }}>
                <Input
                  id='confirmation-input-email'
                  inputStatus='normal'
                  label='Usuario'
                  value={formik.values.user}
                  size='md'
                  type='text'
                  disabled
                />
              </Card>

              <Card margin={{ bottom: '3' }}>
                <Input
                  id='confirmation-input-password'
                  inputStatus={formik.errors.password ? 'error' : 'normal'}
                  label='Crear contraseña'
                  placeholder='Ingresá tu contraseña'
                  size='md'
                  type='password'
                  showPasswordButton
                  onChange={(e) =>
                    formik.setFieldValue('password', e.target.value)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.errors.password && (
                  <Card spacing={{ left: '3' }}>
                    <P
                      type='2'
                      variant={
                        passwordChecks.hasMinLength ? 'success' : 'danger'
                      }
                    >
                      {passwordChecks.hasMinLength ? '✓' : '✕'} 8 dígitos o más.
                    </P>
                    <P
                      type='2'
                      variant={
                        passwordChecks.hasUppercase ? 'success' : 'danger'
                      }
                    >
                      {passwordChecks.hasUppercase ? '✓' : '✕'} Al menos una
                      mayúscula.
                    </P>
                    <P
                      type='2'
                      variant={
                        passwordChecks.hasLowercase ? 'success' : 'danger'
                      }
                    >
                      {passwordChecks.hasLowercase ? '✓' : '✕'} Al menos una
                      minúscula.
                    </P>
                    <P
                      type='2'
                      variant={
                        passwordChecks.hasSpecialChar ? 'success' : 'danger'
                      }
                    >
                      {passwordChecks.hasSpecialChar ? '✓' : '✕'} Al menos un
                      carácter especial.
                    </P>
                  </Card>
                )}
              </Card>

              <Card margin={{ bottom: '3' }}>
                <Input
                  id='confirmation-input-confirm'
                  inputStatus={
                    formik.errors.confirmPassword ? 'error' : 'normal'
                  }
                  label='Confirmar contraseña'
                  placeholder='Repetí tu contraseña'
                  size='md'
                  type='password'
                  showPasswordButton
                  onChange={(e) =>
                    formik.setFieldValue('confirmPassword', e.target.value)
                  }
                />
                {formik.errors.confirmPassword && (
                  <P variant='danger' withMargin={false} type='2'>
                    {formik.errors.confirmPassword}
                  </P>
                )}
              </Card>

              {errorMsg && (
                <P variant='danger' type='1' weight='bold' align='center'>
                  {errorMsg}
                </P>
              )}

              <Card
                display='flex'
                justifyContent='center'
                margin={{ top: '6', bottom: '3' }}
              >
                <Button
                  type='submit'
                  id='confirm-button-send'
                  size='xl'
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Iniciar sesión'
                  )}
                </Button>
              </Card>
            </Card>
          </form>
        )}
      </Card>
    </Card>
  );
};

export default Confirmation;
